import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsElement from '../../core/ids-element';

import '../ids-button/ids-button';
import type IdsButton from '../ids-button/ids-button';

import styles from './ids-scroll-view.scss';

const Base = IdsKeyboardMixin(
  IdsLocaleMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

/**
 * IDS Scroll View Component
 * @type {IdsScrollView}
 * @inherits IdsElement
 * @mixes IdsLocaleMixin
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 * @part container - the parent container element
 * @part scroll-view - the scrolling container
 * @part controls - the control button area
 * @part button - the individual circle buttons in the scroll view
 */
@customElement('ids-scroll-view')
@scss(styles)
export default class IdsScrollView extends Base {
  constructor() {
    super();
  }

  static get attributes() {
    return [
      ...super.attributes,
      attributes.AS_NAV,
      attributes.HIDE_ON_ONE,
      attributes.LOOP,
      attributes.OBJECT_FIT,
      attributes.SHOW_TOOLTIP,
      attributes.SUPPRESS_CONTROLS
    ];
  }

  /**
   * Invoked each time the custom element is add into a document-connected element
   */
  connectedCallback() {
    super.connectedCallback();
    this.#syncNavMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Create the template for the contents
   * @returns {string} The template
   */
  template(): string {
    const controls = !this.asNav ? this.controlsTemplate() : '';

    return `<div class="ids-scroll-view-container${this.asNav ? ' as-nav' : ''}" part="container">
      ${this.asNav ? this.previousThumbnailTemplate() : ''}
      <div class="ids-scroll-view" part="scroll-view" role="complementary" tabindex="-1">
        <slot name="scroll-view-item"></slot>
      </div>
      ${this.asNav ? this.nextThumbnailTemplate() : ''}
      ${controls}
    </div>`;
  }

  /**
   * @returns {Array<string>} Drawer vetoable events
   */
  vetoableEventTypes = [
    'beforefirst',
    'beforeprevious',
    'beforenext',
    'beforelast',
    'beforeslideto'
  ];

  /**
   * Current active slide index
   * @private
   */
  #activeNumber = 0;

  /**
   * State of action by click or keyboard
   * @private
   */
  #isClickOrKey = false;

  /**
   * Sets whether the scroll view should use navigation mode with arrow buttons
   * @param {boolean} value true for navigation mode, false for circle pager mode
   */
  set asNav(value: boolean) {
    const val = stringToBool(value);
    if (val === this.asNav) return;
    if (val) {
      this.setAttribute(attributes.AS_NAV, '');
      this.container?.classList.add(attributes.AS_NAV);
    } else {
      this.removeAttribute(attributes.AS_NAV);
      this.container?.classList.remove(attributes.AS_NAV);
    }
    this.switchMode(val);
  }

  /**
   * Get whether the scroll view is in navigation mode
   * @returns {boolean} true if in navigation mode
   */
  get asNav(): boolean {
    return this.hasAttribute(attributes.AS_NAV);
  }

  /**
   * Get the list of circle buttons
   * @returns {IdsButton[]} the list of circle buttons
   */
  get circleButtons(): IdsButton[] {
    return [...(this.controls?.querySelectorAll<IdsButton>('.circle-button') ?? [])];
  }

  /**
   * Get the controls container
   * @returns {HTMLElement | null | undefined} the controls container element
   */
  get controls(): HTMLElement | null | undefined {
    return this.shadowRoot?.querySelector('.ids-scroll-view-controls');
  }

  /**
   * Configures the circle pager to hide if only one page is present
   * @param {boolean} value true if the circle pager should hide under this condition
   */
  set hideOnOne(value: boolean) {
    this.toggleAttribute(attributes.HIDE_ON_ONE, stringToBool(value));
  }

  /**
   * Get whether to hide controls when only one page is present
   * @returns {boolean} true if should hide on one page
   */
  get hideOnOne() {
    return this.hasAttribute(attributes.HIDE_ON_ONE);
  }

  /**
   * Set the loop, true will loop back after next/previous reached to end
   * @param {boolean} value The value
   */
  set loop(value: boolean) {
    const val = stringToBool(value);
    if (val === this.loop) return;
    if (val) this.setAttribute(attributes.LOOP, '');
    else this.removeAttribute(attributes.LOOP);
  }

  /**
   * Get whether looping is enabled
   * @returns {boolean} true if looping is enabled
   */
  get loop(): boolean {
    return this.hasAttribute(attributes.LOOP);
  }

  /**
   * Set the tooltip for circle buttons
   * @param {boolean | string} value The value
   */
  set showTooltip(value: boolean | string) {
    const val = stringToBool(value);
    if (val === this.showTooltip) return;
    if (val) this.setAttribute(attributes.SHOW_TOOLTIP, '');
    else this.removeAttribute(attributes.SHOW_TOOLTIP);
    this.#setTooltip();
  }

  /**
   * Get whether tooltips are shown for circle buttons
   * @returns {boolean | string} true if tooltips are shown
   */
  get showTooltip(): boolean | string {
    return this.hasAttribute(attributes.SHOW_TOOLTIP);
  }

  /**
   * Set suppress controls for circle buttons
   * @param {boolean} value The value
   */
  set suppressControls(value: boolean) {
    const val = stringToBool(value);
    if (val === this.suppressControls) return;
    if (val) {
      this.setAttribute(attributes.SUPPRESS_CONTROLS, '');
      this.controls?.classList.add(attributes.SUPPRESS_CONTROLS);
    } else {
      this.removeAttribute(attributes.SUPPRESS_CONTROLS);
      this.controls?.classList.remove(attributes.SUPPRESS_CONTROLS);
    }
  }

  /**
   * Get whether controls are suppressed
   * @returns {boolean} true if controls are suppressed
   */
  get suppressControls(): boolean {
    return this.hasAttribute(attributes.SUPPRESS_CONTROLS);
  }

  /** Valid object-fit values */
  static OBJECT_FIT_VALUES = ['contain', 'cover', 'fill', 'none', 'scale-down'];

  /**
   * Set the object-fit style for slotted images
   * @param {string} value - One of: contain, cover, fill, none, scale-down
   */
  set objectFit(value: string) {
    if (value && IdsScrollView.OBJECT_FIT_VALUES.includes(value)) {
      this.setAttribute(attributes.OBJECT_FIT, value);
    } else {
      this.removeAttribute(attributes.OBJECT_FIT);
    }
  }

  get objectFit(): string {
    return this.getAttribute(attributes.OBJECT_FIT) || '';
  }

  /**
   * Move to first slide
   * @returns {void}
   */
  first(): void {
    const args = { activeNumber: this.#activeNumber };
    if (!this.triggerVetoableEvent('beforefirst', args)) return;

    if (this.#activeNumber !== 0) this.#setActive(0, true, true);
  }

  /**
   * Move to previous slide
   * @returns {void}
   */
  previous(): void {
    let num = this.#activeNumber;
    const args = { activeNumber: num };
    if (!this.triggerVetoableEvent('beforeprevious', args)) return;

    if (num <= 0) {
      if (!this.loop) return;
      num = this.circleButtons.length - 1;
    } else num--;

    if (num !== this.#activeNumber) this.#setActive(num, true, true);
  }

  /**
   * Move to next slide
   * @returns {void}
   */
  next(): void {
    let num = this.#activeNumber;
    const args = { activeNumber: num };
    if (!this.triggerVetoableEvent('beforenext', args)) return;

    if (num >= (this.circleButtons.length - 1)) {
      if (!this.loop) return;
      num = 0;
    } else num++;

    if (num !== this.#activeNumber) this.#setActive(num, true, true);
  }

  /**
   * Move to lst slide
   * @returns {void}
   */
  last(): void {
    let num = this.#activeNumber;
    const args = { activeNumber: num };
    if (!this.triggerVetoableEvent('beforelast', args)) return;

    num = this.circleButtons.length - 1;
    if (num !== this.#activeNumber) this.#setActive(num, true, true);
  }

  /**
   * Slide to given slide number
   * @param {number|string} slideNumber The slide number
   * @returns {void}
   */
  slideTo(slideNumber?: number | string): void {
    const args = { activeNumber: this.#activeNumber, slideTo: slideNumber };
    if (!this.triggerVetoableEvent('beforeslideto', args)) return;

    this.#setActive(slideNumber, true, true);
  }

  /**
   * Get the current slide number
   * @returns {number | undefined} the current slide number
   */
  currentSlideNumber(): number | undefined {
    const container = this.container;
    let val;
    if (container) {
      val = container.scrollLeft / container.offsetWidth;
      if (this.localeAPI?.isRTL()) val *= -1;
    }
    return (typeof val === 'number' && !Number.isNaN(val)) ? Math.trunc(val) : undefined;
  }

  /**
   * Move to the given slide number
   * @private
   * @param {number|string} slideNumber The slide number to move
   * @returns {void}
   */
  #moveTo(slideNumber?: number | string): void {
    const container = this.container;
    const num = parseInt((slideNumber as string), 10);

    if (container && !Number.isNaN(num)) {
      let val = container.offsetWidth * num;
      if (this.localeAPI?.isRTL()) val *= -1;

      const left = val - container.scrollLeft;
      if (left) {
        container.scrollBy({ left });
        this.triggerEvent('scrolled', this, {
          detail: { elem: this, activeNumber: this.#activeNumber, left }
        });
      }
    }
  }

  /**
   * Set tooltips for circle buttons
   * @private
   * @returns {void}
   */
  #setTooltip(): void {
    this.circleButtons.forEach((btn) => {
      if (this.showTooltip) btn.setAttribute(attributes.TOOLTIP, (btn.text || '').trim());
      else btn.removeAttribute('tooltip');
      btn.handleTooltipEvents();
    });
  }

  /**
   * Ensures the shadow DOM matches the current asNav state on initial render.
   * @private
   * @returns {void}
   */
  #syncNavMode(): void {
    if (this.asNav) {
      this.switchMode(true, true);
    } else {
      this.#renderCircleButtons();
      this.#attachEventHandlers();
    }
  }

  /**
   * Render the circle buttons
   * @private
   * @returns {void}
   */
  #renderCircleButtons(): void {
    if (this.controls) this.controls.innerHTML = '';

    const items = Array.from(this.children);
    if (this.hideOnOne && items.length <= 1) return;

    items.forEach((item, i) => {
      const cssClass = ` class="circle-button${i === 0 ? ' selected' : ''}"`;
      const ariaSelected = i === 0 ? ' aria-selected="true"' : '';
      const label = item.getAttribute('label') || item.getAttribute('alt') || '';
      const tooltip = this.showTooltip ? `tooltip="${label}"` : '';

      this.controls?.insertAdjacentHTML('beforeend', `
        <ids-button data-slide-number="${i}" part="button" exportparts="button: scroll-view-button" role="tab"${cssClass}${ariaSelected}${tooltip}>
          <ids-icon icon="empty-circle-filled"></ids-icon>
          <span class="audible">${label}</span>
        </ids-button>`);
    });
  }

  /**
   * Set as active slide and optionly set focus
   * @private
   * @param {number | string} slideNumber The slide number
   * @param {boolean} isMove If true, set to move
   * @param {boolean} isFocus If true, set to focus
   * @returns {void}
   */
  #setActive(slideNumber?: number | string, isMove = false, isFocus = false): void {
    const buttons = this.circleButtons;
    const num = parseInt((slideNumber as string), 10);

    if (!Number.isNaN(num) && num !== this.#activeNumber) {
      const active = buttons[this.#activeNumber];
      const btn = buttons[num];
      this.#activeNumber = num;

      active?.classList.remove('selected');
      active?.removeAttribute(htmlAttributes.ARIA_SELECTED);

      btn?.classList.add('selected');
      btn?.setAttribute(htmlAttributes.ARIA_SELECTED, 'true');

      if (isMove) this.#moveTo(num);
      if (isFocus) btn?.focus();
    }
  }

  /**
   * Establish Internal Event Handlers
   * @private
   * @returns {void}
   */
  #attachEventHandlers(): void {
    // Handle click event
    this.offEvent('click.scrollview', this.controls);
    this.onEvent('click.scrollview', this.controls, (e: MouseEvent) => {
      e.preventDefault();
      this.#isClickOrKey = true;
      this.#setActive((e.target as any)?.getAttribute('data-slide-number'), true, false);
    });

    // Handle arrow keys
    this.unlisten('ArrowLeft');
    this.unlisten('ArrowRight');
    this.unlisten('Enter');
    this.listen(['ArrowLeft', 'ArrowRight', 'Enter'], this.controls, (e: any) => {
      this.#isClickOrKey = true;

      if (e.key === 'ArrowLeft') this.previous();
      if (e.key === 'ArrowRight') this.next();
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
      }
    });

    const itemSlot = this.container?.querySelector('slot[name="scroll-view-item"]');
    this.offEvent('slotchange.scrollview', itemSlot);
    this.onEvent('slotchange', itemSlot, () => this.#handleSlotChange());

    // Handle scroll-end, after snap scrolling event is complete
    // https://stackoverflow.com/a/66029649
    this.offEvent('scroll.scrollview', this.container);
    this.onEvent('scroll.scrollview', this.container, (e: any) => {
      const target = e.target;
      if (target && (target.scrollLeft % target.offsetWidth === 0)) {
        if (!this.#isClickOrKey) this.#setActive(this.currentSlideNumber(), false, true);
        this.#isClickOrKey = false;
      }
    });

    this.#handleNavEvents();
  }

  #handleNavEvents() {
    const leftBtn = this.shadowRoot?.querySelector<HTMLElement>('#scroll-start');
    const rightBtn = this.shadowRoot?.querySelector<HTMLElement>('#scroll-end');
    const scrollArea = this.shadowRoot?.querySelector('.ids-scroll-view');

    this.offEvent('click.leftarrow', leftBtn);
    this.onEvent('click.leftarrow', leftBtn, () => {
      const scrollAmount = this.localeAPI?.isRTL() ? 100 : -100;
      scrollArea?.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });

    this.offEvent('click.rightarrow', rightBtn);
    this.onEvent('click.rightarrow', rightBtn, () => {
      const scrollAmount = this.localeAPI?.isRTL() ? -100 : 100;
      scrollArea?.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });

    // Show/hide arrows on resize or scroll
    const checkVisibility = () => {
      if (!scrollArea) return;

      const isRTL = this.localeAPI?.isRTL();
      const scrollEl = scrollArea as HTMLElement;
      const scrollLeft = scrollEl.scrollLeft || 0;
      const clientWidth = scrollEl.clientWidth || 0;
      const scrollWidth = scrollEl.scrollWidth || 0;

      // Hide both buttons if content fits within container
      if (scrollWidth <= clientWidth) {
        leftBtn?.classList.add('hidden');
        rightBtn?.classList.add('hidden');
        return;
      }

      // Show both buttons if content overflows
      if (scrollWidth > clientWidth) {
        leftBtn?.classList.remove('hidden');
        rightBtn?.classList.remove('hidden');
      }

      // Determine which buttons should be disabled based on scroll position
      if (isRTL) {
        // In RTL, scrollLeft starts at 0 (rightmost) and goes negative as you scroll left
        const maxScroll = scrollWidth - clientWidth;

        // Left button (scrolls right in RTL) - disabled when at rightmost position
        if (scrollLeft >= -40) {
          leftBtn?.setAttribute(attributes.DISABLED, '');
        } else {
          leftBtn?.removeAttribute(attributes.DISABLED);
        }

        // Right button (scrolls left in RTL) - disabled when at leftmost position
        if (Math.abs(scrollLeft) >= maxScroll - 40) {
          rightBtn?.setAttribute(attributes.DISABLED, '');
        } else {
          rightBtn?.removeAttribute(attributes.DISABLED);
        }
      } else {
        // LTR behavior (original logic)
        // Left button disabled when at leftmost position
        if (scrollLeft <= 40) {
          leftBtn?.setAttribute(attributes.DISABLED, '');
        } else {
          leftBtn?.removeAttribute(attributes.DISABLED);
        }

        // Right button disabled when at rightmost position
        if (scrollLeft + clientWidth >= scrollWidth - 40) {
          rightBtn?.setAttribute(attributes.DISABLED, '');
        } else {
          rightBtn?.removeAttribute(attributes.DISABLED);
        }
      }
    };

    this.onEvent('scroll.nav', scrollArea, checkVisibility);
    this.onEvent('resize.nav', window, checkVisibility);
    checkVisibility();
  }

  #handleSlotChange() {
    if (this.asNav) return;

    this.first();
    this.#renderCircleButtons();
  }

  /**
   * Clean up all event handlers
   * @private
   * @returns {void}
   */
  #cleanupEventHandlers(): void {
    this.offEvent('click.scrollview');
    this.offEvent('click.leftarrow');
    this.offEvent('click.rightarrow');
    this.offEvent('scroll.scrollview');
    this.offEvent('slotchange.scrollview');
    this.offEvent('scroll.nav');
    this.offEvent('resize.nav', window);
    this.unlisten('ArrowLeft');
    this.unlisten('ArrowRight');
    this.unlisten('Enter');
  }

  /**
   * Returns the HTML template for the previous thumbnail navigation button
   * @returns {string} HTML template for previous button
   */
  previousThumbnailTemplate(): string {
    return '<ids-button id="scroll-start" class="scroll-start hidden"><ids-icon icon="chevron-left"></ids-icon><ids-text audible translate-text="true">Previous Thumbnail</ids-text></ids-button>';
  }

  /**
   * Returns the HTML template for the next thumbnail navigation button
   * @returns {string} HTML template for next button
   */
  nextThumbnailTemplate(): string {
    return '<ids-button id="scroll-end" class="scroll-end hidden"><ids-icon icon="chevron-right"></ids-icon><ids-text audible translate-text="true">Next Thumbnail</ids-text></ids-button>';
  }

  /**
   * Returns the HTML template for the circle button controls container
   * @returns {string} HTML template for controls container
   */
  controlsTemplate(): string {
    let controlsClass = 'ids-scroll-view-controls';
    if (this.suppressControls) controlsClass += ` ${attributes.SUPPRESS_CONTROLS}`;
    return `<div class="${controlsClass}" part="controls" role="tablist"></div>`;
  }

  /**
   * Switch between circle pager and thumbnail navigation
   * @param {boolean} useThumbnailNav - true for thumbnail navigation, false for circle pager
   * @param {boolean} force - true to skip the early return guard and force a re-render
   */
  switchMode(useThumbnailNav: boolean, force = false): void {
    if (!force && this.asNav === useThumbnailNav) return;

    this.#cleanupEventHandlers();

    const currentActive = this.#activeNumber;
    const scrollPosition = this.container?.scrollLeft || 0;

    const container = this.shadowRoot?.querySelector('.ids-scroll-view-container');
    const controls = this.shadowRoot?.querySelector('.ids-scroll-view-controls');
    const scrollView = container?.querySelector('.ids-scroll-view');

    if (useThumbnailNav) {
      this.asNav = true;
      container?.classList.add('as-nav');
      scrollView?.classList.remove('media-circle-pager');

      controls?.remove();

      if (!this.shadowRoot?.querySelector('#scroll-start')) {
        container?.insertAdjacentHTML('afterbegin', this.previousThumbnailTemplate());
      }
      if (!this.shadowRoot?.querySelector('#scroll-end')) {
        container?.insertAdjacentHTML('beforeend', this.nextThumbnailTemplate());
      }
    } else {
      this.asNav = false;
      container?.classList.remove('as-nav');
      scrollView?.classList.add('media-circle-pager');

      this.shadowRoot?.querySelectorAll('#scroll-start, #scroll-end').forEach((btn) => btn.remove());

      if (!controls) {
        container?.insertAdjacentHTML('beforeend', this.controlsTemplate());
      }
    }

    this.#renderCircleButtons();
    this.#attachEventHandlers();

    this.#setActive(currentActive, false, false);
    if (this.container) this.container.scrollLeft = scrollPosition;
  }
}
